import { CopyButton, Row } from "@gadgetinc/widgets";
import { ChangeLanguageIcon } from "@gadgetinc/widgets/src/icons/ChangeLanguageIcon";
import { useStyletron } from "baseui";
import { Button, KIND as ButtonKind, SIZE } from "baseui/button";
import { StatefulMenu } from "baseui/menu";
import { PLACEMENT, StatefulPopover } from "baseui/popover";
import { build, initialize } from "esbuild-wasm";
import esbuildWasm from "esbuild-wasm/esbuild.wasm?url";
import { defaultTo, isString, mapValues, memoize } from "lodash";
import { observer } from "mobx-react-lite";
import type { ReactNode } from "react";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DevelopmentEnvironmentName } from "state-trees/src/Environment";
import { prettifyCodeExample } from "state-trees/src/prettifyCodeExample";
import { CodeBlockFilename, CodeBlockTitle, CodeBlockTitleBar, CodeBlockTitleBarSpacer } from "web/src/components/markdown/CodeBlock";
import { CodeHighlight } from "web/src/components/markdown/CodeHighlight";
import { LanguageIcon, LanguageIndicator, languageLabel } from "web/src/components/markdown/LanguageIndicator";
import { DocsContext } from "../chrome/DocsContext";
import { CodeBlock, CodeBlockContainer, handleCopyWithToast } from "./CodeBlock";
import { OpenInGraphQLPlaygroundButton, OpenInJavaScriptPlaygroundButton } from "./OpenInPlaygroundButton";

const memoizedTransform = memoize(async (options: { code: string }) => {
  // Since esbuild strips out all comments, we need to replace them with legal comments and esbuild will keep them.
  // Reference: https://esbuild.github.io/api/#legal-comments
  const modifiedCode = options.code.replaceAll("// ", "//!@comment ").replaceAll("/**", "/*!@comment ").replaceAll("\n\n", "//!@newline\n");

  // We need to use `build` instead of `transform` because `transform` modifies the `export` syntax and groups them into a single `export` object.
  const buildResult = await build({
    stdin: {
      contents: modifiedCode,
      loader: "tsx",
      resolveDir: ".",
    },
    write: false,
    jsx: "preserve",
    legalComments: "inline",
    minify: false,
  });
  const code = buildResult.outputFiles[0].text;

  // Then we remove our markers and restore the original comments and newlines
  return code
    .replaceAll("//!@comment ", "// ")
    .replaceAll("/*!@comment ", "/**")
    .replaceAll("//!@newline\n", "\n")
    .replaceAll("/* @__PURE__ */ ", "");
});

let esbuildInitializePromise: Promise<void> | undefined;

const maybePrettify = (code: string, language: string, prettify?: boolean) => {
  return defaultTo(prettify, true) ? prettifyCodeExample(code, language, 85) : code;
};

export const TabbedCodeExample = observer(
  (props: {
    title?: ReactNode;
    filename?: string;
    examples: { [language: string]: string | { query: string; variables: any } };
    detachSelector?: boolean;
    showGqlPlaygroundLink?: boolean;
    showJsPlaygroundLink?: boolean;
    /** Set by the remark plugin when this tabbed example is auto-generated from a typescript code block */
    isAutoSelector?: boolean;
    prettify?: boolean;
  }) => {
    const [css, $theme] = useStyletron();
    const { currentApp } = useContext(DocsContext);
    const typescriptFlag = currentApp.languagePreference === "typescript";
    const originalLanguages = Object.keys(props.examples);
    const availableLanguages = useMemo(() => {
      let list = originalLanguages;
      if (list.includes("tsx") && !list.includes("react")) {
        list.push("react");
      }
      if (list.includes("typescript") && !list.includes("javascript")) {
        list.push("javascript");
      }

      if (!typescriptFlag) {
        list = list.filter((language) => language !== "typescript" && language !== "tsx");
      }

      return list;
    }, [originalLanguages, typescriptFlag]);

    // eslint-disable-next-line @typescript-eslint/consistent-type-imports
    const [wasmInitialized, setWasmInitialized] = useState<boolean>(false);

    useEffect(() => {
      async function initializeWasmModules() {
        if (!esbuildInitializePromise) {
          // the `initialize` function can only be called once or it will throw an error
          esbuildInitializePromise = initialize({
            wasmURL: esbuildWasm,
          });
        }

        await esbuildInitializePromise;
        setWasmInitialized(true);
      }
      void initializeWasmModules();
    }, []);

    const [activeLanguage, setActiveLanguage] = useState<string>(
      props.detachSelector ? availableLanguages[0] : currentApp.languagePreference
    );

    useEffect(() => {
      if (!availableLanguages.includes(activeLanguage)) {
        setActiveLanguage(availableLanguages[0]);
      }
    }, [availableLanguages, activeLanguage]);

    let filename = props.filename;
    if (typeof filename === "string" && filename.match(/\.tsx?/) && activeLanguage === "javascript") {
      filename = filename.replace(".ts", ".js");
    }

    const [showSelector, setShowSelector] = useState<boolean>(availableLanguages.length > 1);

    useEffect(() => {
      if (!typescriptFlag && props.isAutoSelector) {
        setShowSelector(false);
        setActiveLanguage("javascript");
      }
    }, [typescriptFlag, props.isAutoSelector]);

    const [jsExample, setJsExample] = useState<string | null>(null);
    const [jsxExample, setJsxExample] = useState<string | null>(null);

    useEffect(() => {
      async function transformExamples() {
        if (!wasmInitialized) {
          return;
        }
        if (originalLanguages.includes("typescript")) {
          setJsExample(await memoizedTransform({ code: props.examples["typescript"] as string }));
        }
        if (originalLanguages.includes("tsx")) {
          setJsxExample(await memoizedTransform({ code: props.examples["tsx"] as string }));
        }
      }
      void transformExamples();
    }, [wasmInitialized, originalLanguages, props.examples]);

    if (wasmInitialized && originalLanguages.includes("typescript") && jsExample) {
      props.examples["javascript"] = jsExample;
    }

    if (wasmInitialized && originalLanguages.includes("tsx") && jsxExample) {
      props.examples["react"] = jsxExample;
    }

    const menuItems = availableLanguages.map((language) => ({
      label: language,
      onClick: () => setActiveLanguage(language),
    }));

    const examples = mapValues(props.examples, (example, language) =>
      isString(example)
        ? maybePrettify(example, language, props.prettify)
        : {
            query: maybePrettify(example.query, "graphql", props.prettify),
            variables: example.variables,
          }
    );

    const copyCode = useCallback(() => {
      const value = examples[activeLanguage];
      let code;
      if (isString(value)) {
        code = value;
      } else {
        code = value.query;
      }

      void handleCopyWithToast(code);
    }, [examples, activeLanguage]);

    let codeForDocsContentExtraction: string | undefined = undefined;
    let languageForDocsContentExtraction: string | undefined = undefined;
    if (examples["typescript"]) {
      codeForDocsContentExtraction = examples["typescript"] as string;
      languageForDocsContentExtraction = "typescript";
    } else if (examples["javascript"]) {
      codeForDocsContentExtraction = examples["javascript"] as string;
      languageForDocsContentExtraction = "javascript";
    }

    return (
      <CodeBlockContainer rawCode={codeForDocsContentExtraction} language={languageForDocsContentExtraction}>
        <CodeBlockTitleBar>
          {props.title && <CodeBlockTitle>{props.title}</CodeBlockTitle>}
          {filename && <CodeBlockFilename>{filename}</CodeBlockFilename>}
          <CodeBlockTitleBarSpacer />
          {showSelector ? (
            <StatefulPopover
              placement={PLACEMENT.bottom}
              overrides={{
                Body: {
                  style: {
                    backgroundColor: $theme.colors.primary600,
                    marginLeft: $theme.sizing.scale500,
                    marginRight: $theme.sizing.scale500,
                    marginTop: $theme.sizing.scale500,
                    marginBottom: $theme.sizing.scale500,
                    borderRadius: $theme.borders.radius200,
                  },
                },
              }}
              content={({ close }) => (
                <StatefulMenu
                  overrides={{
                    Option: {
                      props: {
                        getItemLabel: (item: { label: string }) => (
                          <Row $gap={$theme.sizing.scale300}>
                            <LanguageIcon language={item.label} />
                            {languageLabel(item.label)}
                          </Row>
                        ),
                      },
                      style: {
                        ...$theme.typography.MonoLabelXSmall,
                      },
                    },
                  }}
                  onItemSelect={({ item }) => {
                    setActiveLanguage(item.label);
                    close();
                  }}
                  items={menuItems}
                />
              )}
              accessibilityType="menu"
            >
              <Button
                kind={ButtonKind.minimal}
                size={SIZE.mini}
                endEnhancer={<ChangeLanguageIcon $style={{ marginLeft: "-4px" }} />}
                overrides={{
                  BaseButton: {
                    style: ({ $theme }) => ({
                      color: $theme.colors.primary300,
                      ...$theme.typography.MonoLabelXSmall,
                      ":hover": {
                        backgroundColor: "rgba(255,255,255,0.1)",
                      },
                    }),
                  },
                }}
              >
                {languageLabel(activeLanguage)}
              </Button>
            </StatefulPopover>
          ) : (
            <LanguageIndicator color={$theme.colors.primary300}>{languageLabel(activeLanguage)}</LanguageIndicator>
          )}
          <CopyButton onClick={copyCode} />
        </CodeBlockTitleBar>
        {Object.entries(examples).map(([language, data]) => {
          let code;
          let variables;
          if (isString(data)) {
            code = data;
          } else {
            code = data.query;
            variables = data.variables;
          }

          return (
            <div key={language} className={css({ display: activeLanguage == language ? "auto" : "none", overflow: "auto" })}>
              {props.showJsPlaygroundLink && language == "javascript" && (
                <OpenInJavaScriptPlaygroundButton
                  code={code}
                  tabName={props.title?.toString() ?? "JavaScript Example"}
                  primaryDomain={currentApp.productionRenderURL}
                  environment={currentApp.environmentName ?? DevelopmentEnvironmentName}
                  $style={{
                    float: "right",
                    marginRight: $theme.sizing.scale500,
                  }}
                />
              )}
              {props.showGqlPlaygroundLink && language == "graphql" && (
                <OpenInGraphQLPlaygroundButton
                  graphql={code}
                  variables={variables}
                  tabName={props.title?.toString() ?? "GraphQL Example"}
                  endpoint={currentApp.productionGraphQLEndpoint}
                  environment={currentApp.environmentName ?? DevelopmentEnvironmentName}
                  $style={{
                    float: "right",
                    marginRight: $theme.sizing.scale500,
                  }}
                />
              )}
              <div
                onClick={copyCode}
                className={css({
                  paddingLeft: $theme.sizing.scale400,
                  paddingRight: $theme.sizing.scale400,
                  paddingBottom: $theme.sizing.scale600,
                })}
              >
                <CodeHighlight language={language}>{code}</CodeHighlight>
              </div>
              {variables && (
                <CodeBlock title="Variables" language="json">
                  {prettifyCodeExample(JSON.stringify(variables), "json", 85)}
                </CodeBlock>
              )}
            </div>
          );
        })}
      </CodeBlockContainer>
    );
  }
);
